import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Check, Copy, Users } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";

import { Button } from "~/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "~/components/ui/card";
import { Input } from "~/components/ui/input";
import { Skeleton } from "~/components/ui/skeleton";
import { getUserReferralInfo } from "~/lib/functions/referrals";
import { buildReferralUrl } from "~/lib/shared/referral";

const referralQueryOptions = {
  queryKey: ["user", "referral-info"] as const,
  queryFn: () => getUserReferralInfo(),
};

export const Route = createFileRoute("/_user/referrals")({
  staticData: { title: "Referrals" },
  loader: async ({ context }) => {
    await context.queryClient.ensureQueryData(referralQueryOptions);
    return {};
  },
  component: ReferralsPage,
});

function formatName(firstName: string | null, lastName: string | null): string {
  const name = [firstName, lastName].filter(Boolean).join(" ").trim();
  return name || "New member";
}

function ReferralsSkeleton() {
  return (
    <div role="status" aria-label="Loading referrals">
      <div className="mb-8">
        <h1 className="font-heading text-2xl font-medium">Referrals</h1>
        <div className="mt-2 max-w-2xl space-y-2">
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-2/3" />
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-4 w-4" />
            Your referral link
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col gap-6">
            <div className="flex flex-wrap items-center gap-2">
              <Skeleton className="h-9 w-full max-w-md" />
              <Skeleton className="size-9" />
            </div>
            <div className="flex flex-col gap-2">
              <Skeleton className="h-5 w-24" />
              <Skeleton className="h-9 w-full rounded-lg" />
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function ReferralsPage() {
  const { data } = useQuery(referralQueryOptions);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) {
      return;
    }
    const timer = setTimeout(() => setCopied(false), 2000);
    return () => clearTimeout(timer);
  }, [copied]);

  if (!data) {
    return <ReferralsSkeleton />;
  }

  const referralUrl = buildReferralUrl(data.referralCode);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(referralUrl);
    setCopied(true);
    toast.success("Referral link copied");
  };

  return (
    <div>
      <div className="mb-8">
        <h1 className="font-heading text-2xl font-medium">Referrals</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Share Dream Coach Match with friends and colleagues. Anyone who signs
          up through your link is credited to you.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-4 w-4" />
            Your referral link
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col gap-6">
            <div className="flex flex-wrap items-center gap-2">
              <Input
                readOnly
                value={referralUrl}
                className="max-w-md font-mono text-sm"
                onFocus={(event) => event.currentTarget.select()}
              />
              <Button size="sm" onClick={handleCopy} title="Copy referral link">
                {copied ? (
                  <Check className="h-4 w-4" />
                ) : (
                  <Copy className="h-4 w-4" />
                )}
              </Button>
            </div>

            <div className="flex flex-col gap-2">
              <h4 className="font-medium">
                {data.referredUsers.length} referred
              </h4>
              {data.referredUsers.map((referred) => (
                <div
                  key={referred.id}
                  className="rounded-lg border bg-card/50 px-3 py-2 text-sm"
                >
                  {formatName(referred.firstName, referred.lastName)}
                </div>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
